<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import { toast } from 'vue-sonner'
import { Loader2 } from 'lucide-vue-next'
import router from '@/router'
import { login } from '@/services/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogTitle,
  DialogTrigger,
  DialogContent,
  DialogDescription,
  DialogHeader
} from '@/components/ui/dialog'
import { Carousel } from 'ant-design-vue'
import { useMessageStore } from '@/store/messages'

const redirect = router.currentRoute.value.query.redirect?.toString()
const loggingIn = ref(false)
const {messages}= useMessageStore();
const version = VERSION.npm_package_version;
async function handleLogin(e: Event) {
  loggingIn.value = true
  e.preventDefault()
  const email = (e.target as HTMLFormElement).email.value
  const password = (e.target as HTMLFormElement).password.value
  try {
    const { statusCode, response_message } = await login(email, password)

    if (statusCode === 200) {
      toast.success('Logged in successfully')
      router.push(redirect ?? '/app')
    } else toast.error(response_message)
    loggingIn.value = false
  } catch (error) {
    console.error(error)
  }
}
</script>

<template>
  <div class="min-h-screen w-full lg:grid lg:grid-cols-2">
    <div class="flex items-center justify-center py-12">
      <div class="mx-auto grid w-[350px] gap-6">
        <img src="https://cdn.aplos-nca.com/assets/ColorLogo.svg" class="md:mx-none mx-auto mb-8 h-14 dark:hidden" />
        <img src="https://cdn.aplos-nca.com/assets/MonoLogoWhite.svg" class="md:mx-none mx-auto mb-8 hidden h-14 dark:block" />
        <div class="w-full overflow-hidden">
          <Carousel autoplay class="rounded-md">
            <div v-for="slide in messages.logOutMessages">
              <a-tooltip v-if="slide.message.length > 48" :title="slide.message" placement="top"
                overlay-class-name="tooltip-style">
                <h3
                  class='p-4 text-center rounded-lg border shadow-sm font-semibold tracking-tight text-white text-card-foreground w-full max-w-full break-words'
                  :style="{ backgroundColor: slide.color === 'red' ? '#ff5757' : slide.color === 'green' ? 'rgb(90 167 69)' : slide.color === 'blue' ? '#01508d' : '#01508d' }">
                  {{ slide.message.slice(0, 48) + '...' }}
                </h3>
              </a-tooltip>
              <h3 v-else
                class='p-4 text-center rounded-lg border shadow-sm font-semibold tracking-tight text-white text-card-foreground w-full max-w-full break-words'
                :style="{ backgroundColor: slide.color === 'red' ? '#ff5757' : slide.color === 'green' ? 'rgb(90 167 69)' : slide.color === 'blue' ? '#01508d' : '#01508d' }">
                {{ slide.message }}
              </h3>
            </div>
          </Carousel>
        </div>
        <div class="grid gap-2">
          <h1 class="text-3xl font-bold">Login</h1>
          <p class="text-balance text-muted-foreground">
            Enter your email below to login to your account
          </p>
        </div>
        <form @submit.prevent="handleLogin" class="grid gap-4">
          <div class="grid gap-2">
            <Label for="email">Email</Label>
            <Input
              id="email"
              type="email"
              autocomplete="username"
              placeholder="m@example.com"
              required
            />
          </div>
          <div class="grid gap-2">
            <div class="flex items-center">
              <Label for="password">Password</Label>
              <RouterLink to="/forgot-password" class="ml-auto inline-block text-sm underline">
                Forgot your password?
              </RouterLink>
            </div>
            <Input autocomplete="current-password" id="password" type="password" required />
          </div>
          <Button type="submit" class="w-full mt-4" :disabled="loggingIn">
            <Loader2 v-if="loggingIn" class="mr-2 size-4 animate-spin" /> Login
          </Button>
        </form>
        <div class="mt-4 text-center text-sm">
          Don't have an account?
          <Dialog>
            <DialogTrigger as-trigger class="underline"> Sign up </DialogTrigger>
            <DialogContent class="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle> Aplos NCA Subscriptions </DialogTitle>
                <DialogDescription class="space-y-3">
                  <p>
                    We have plans for individuals and teams - everything you need to perform lightning-fast pharmacokinetic analysis.
                  </p>
                  <p>
                    <a
                      class="text-blue-600 hover:text-blue-700 hover:underline"
                      href="https://aplosanalytics.com/plans"
                      target="_blank"
                    >
                    Visit our website</a
                    > and sign up for the plan that is right for you.
                  </p>
                </DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </div>

        <div class="mt-4 text-center text-xs muted-text">
        Version {{ version }}
        </div>
      </div>
    </div>
    <div class="hidden max-h-screen py-10 lg:block">
      <div
        class="flex h-full flex-col justify-around rounded-l-3xl bg-gradient-to-r from-[#0097b27c] to-[#7ed95778] p-20"
      >
        <h2 class="text-4xl font-semibold text-white">Streamline your workflow with Aplos NCA</h2>
        <div>
          <img src="https://cdn.aplos-nca.com/assets/login.png" class="max-h-[60vh] object-contain" />
        </div>
      </div>
    </div>
  </div>
</template>
